/**
 * Task A2A-A (#1959) — Tipi e schemi Zod del protocollo A2A (Agent-to-Agent).
 *
 * Sottoinsieme v1 della spec A2A pubblica (https://a2aproject.github.io/A2A):
 *   - JSON-RPC 2.0 sync (no SSE in questo round)
 *   - Metodi: `message/send`, `tasks/get`, `tasks/cancel`, `agent/discover`
 *   - Agent Card minimale (name, description, url, capabilities, skills)
 *
 * Backend e frontend importano da `@easyflow/shared/a2a` per garantire
 * un contratto unico. Mai usare `any`: ogni payload passa per uno Zod schema
 * al boundary HTTP.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Agent kind (allineato col CHECK constraint della migration `a2a_agents`)
// ---------------------------------------------------------------------------
export const A2A_AGENT_KINDS = [
  'ai-dashboard',
  'elevenlabs',
  'openai-realtime',
  'chatbot-rag',
  'workflow-builder',
  'orchestrator',
  'custom',
  // Task #3414 — agente di sistema Incentivazione (commissioning-builder).
  'commissioning-builder',
] as const;
export type A2AAgentKind = (typeof A2A_AGENT_KINDS)[number];
export const A2AAgentKindSchema = z.enum(A2A_AGENT_KINDS);

export const A2A_AUTH_MODES = ['service-jwt', 'none'] as const;
export type A2AAuthMode = (typeof A2A_AUTH_MODES)[number];
export const A2AAuthModeSchema = z.enum(A2A_AUTH_MODES);

// ---------------------------------------------------------------------------
// Agent Card (sottoinsieme A2A spec)
// ---------------------------------------------------------------------------
export const A2ASkillSchema = z.object({
  id: z.string().min(1).max(120),
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  tags: z.array(z.string().max(64)).max(32).optional(),
  inputModes: z.array(z.string().max(64)).max(16).optional(),
  outputModes: z.array(z.string().max(64)).max(16).optional(),
});
export type A2ASkill = z.infer<typeof A2ASkillSchema>;

export const A2ACapabilitiesSchema = z.object({
  streaming: z.boolean().default(false),
  pushNotifications: z.boolean().default(false),
  stateTransitionHistory: z.boolean().default(false),
});
export type A2ACapabilities = z.infer<typeof A2ACapabilitiesSchema>;

export const A2AAgentCardSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  url: z.string().url(),
  version: z.string().max(32).default('1.0.0'),
  provider: z
    .object({
      organization: z.string().max(255).optional(),
      url: z.string().url().optional(),
    })
    .optional(),
  capabilities: A2ACapabilitiesSchema,
  defaultInputModes: z.array(z.string().max(64)).max(16).default(['text']),
  defaultOutputModes: z.array(z.string().max(64)).max(16).default(['text']),
  skills: z.array(A2ASkillSchema).max(64).default([]),
});
export type A2AAgentCard = z.infer<typeof A2AAgentCardSchema>;

// ---------------------------------------------------------------------------
// Registry record (riflesso 1:1 della riga DB)
// ---------------------------------------------------------------------------
export const A2AAgentRecordSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  name: z.string().min(1).max(255),
  slug: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/, 'slug deve essere kebab-case ASCII'),
  agent_kind: A2AAgentKindSchema,
  card_json: A2AAgentCardSchema,
  endpoint_url: z.string().min(1).max(2048),
  auth_mode: A2AAuthModeSchema,
  capabilities: z.array(z.string().max(120)).max(64),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type A2AAgentRecord = z.infer<typeof A2AAgentRecordSchema>;

export const A2AAgentCreateInputSchema = z.object({
  name: z.string().min(1).max(255),
  slug: A2AAgentRecordSchema.shape.slug,
  agent_kind: A2AAgentKindSchema,
  card_json: A2AAgentCardSchema,
  endpoint_url: z
    .string()
    .min(1)
    .max(2048)
    .refine(
      (v) => v.startsWith('local://') || v.startsWith('https://') || v.startsWith('http://'),
      'endpoint_url deve essere http(s):// o local://<key>',
    ),
  auth_mode: A2AAuthModeSchema.default('service-jwt'),
  capabilities: z.array(z.string().max(120)).max(64).default([]),
  is_active: z.boolean().default(true),
});
export type A2AAgentCreateInput = z.infer<typeof A2AAgentCreateInputSchema>;

export const A2AAgentUpdateInputSchema = A2AAgentCreateInputSchema.partial();
export type A2AAgentUpdateInput = z.infer<typeof A2AAgentUpdateInputSchema>;

// ---------------------------------------------------------------------------
// JSON-RPC 2.0
// ---------------------------------------------------------------------------
export const JSONRPC_VERSION = '2.0' as const;

export const A2AMethodSchema = z.enum([
  'message/send',
  'tasks/get',
  'tasks/cancel',
  'agent/discover',
]);
export type A2AMethod = z.infer<typeof A2AMethodSchema>;

export const A2AJsonRpcIdSchema = z.union([z.string().min(1).max(120), z.number().int()]);
export type A2AJsonRpcId = z.infer<typeof A2AJsonRpcIdSchema>;

// Message Part — minimal: testo + data structured + file reference.
export const A2AMessagePartSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('text'),
    text: z.string().max(64_000),
  }),
  z.object({
    kind: z.literal('data'),
    data: z.record(z.string(), z.unknown()),
  }),
  z.object({
    kind: z.literal('file'),
    file: z.object({
      name: z.string().max(255),
      mime_type: z.string().max(255),
      uri: z.string().max(2048),
    }),
  }),
]);
export type A2AMessagePart = z.infer<typeof A2AMessagePartSchema>;

export const A2AMessageRoleSchema = z.enum(['user', 'agent']);
export type A2AMessageRole = z.infer<typeof A2AMessageRoleSchema>;

export const A2AMessageSchema = z.object({
  message_id: z.string().uuid(),
  role: A2AMessageRoleSchema,
  parts: z.array(A2AMessagePartSchema).min(1).max(64),
  context_id: z.string().max(255).optional(),
  task_id: z.string().uuid().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type A2AMessage = z.infer<typeof A2AMessageSchema>;

export const A2ATaskStateSchema = z.enum([
  'submitted',
  'working',
  'completed',
  'failed',
  'canceled',
]);
export type A2ATaskState = z.infer<typeof A2ATaskStateSchema>;

export const A2ATaskSchema = z.object({
  id: z.string().uuid(),
  agent_id: z.string().uuid(),
  state: A2ATaskStateSchema,
  context_id: z.string().max(255).optional(),
  messages: z.array(A2AMessageSchema).max(256),
  artifacts: z
    .array(
      z.object({
        artifact_id: z.string().uuid(),
        name: z.string().max(255).optional(),
        parts: z.array(A2AMessagePartSchema).min(1).max(64),
      }),
    )
    .max(64)
    .optional(),
  error: z
    .object({
      code: z.number().int(),
      message: z.string().max(2000),
    })
    .optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type A2ATask = z.infer<typeof A2ATaskSchema>;

// JSON-RPC envelopes -------------------------------------------------------
export const A2AJsonRpcRequestSchema = z.object({
  jsonrpc: z.literal(JSONRPC_VERSION),
  id: A2AJsonRpcIdSchema,
  method: A2AMethodSchema,
  params: z.record(z.string(), z.unknown()).default({}),
});
export type A2AJsonRpcRequest = z.infer<typeof A2AJsonRpcRequestSchema>;

export const A2AJsonRpcErrorSchema = z.object({
  code: z.number().int(),
  message: z.string().max(2000),
  data: z.unknown().optional(),
});
export type A2AJsonRpcError = z.infer<typeof A2AJsonRpcErrorSchema>;

export const A2AJsonRpcResponseSchema = z.object({
  jsonrpc: z.literal(JSONRPC_VERSION),
  id: A2AJsonRpcIdSchema.nullable(),
  result: z.unknown().optional(),
  error: A2AJsonRpcErrorSchema.optional(),
});
export type A2AJsonRpcResponse = z.infer<typeof A2AJsonRpcResponseSchema>;

// Method param/result types -----------------------------------------------
export const A2AMessageSendParamsSchema = z.object({
  agent_id: z.string().uuid(),
  message: A2AMessageSchema,
});
export type A2AMessageSendParams = z.infer<typeof A2AMessageSendParamsSchema>;

export const A2ATaskIdParamsSchema = z.object({
  task_id: z.string().uuid(),
});
export type A2ATaskIdParams = z.infer<typeof A2ATaskIdParamsSchema>;

export const A2ADiscoverParamsSchema = z.object({
  agent_kind: A2AAgentKindSchema.optional(),
  capability: z.string().max(120).optional(),
});
export type A2ADiscoverParams = z.infer<typeof A2ADiscoverParamsSchema>;

export const A2ADiscoverResultSchema = z.object({
  agents: z.array(
    z.object({
      id: z.string().uuid(),
      slug: z.string(),
      name: z.string(),
      agent_kind: A2AAgentKindSchema,
      capabilities: z.array(z.string()),
      card_url: z.string(),
    }),
  ),
});
export type A2ADiscoverResult = z.infer<typeof A2ADiscoverResultSchema>;

// JSON-RPC error codes (A2A v1)
export const A2A_ERROR = {
  PARSE_ERROR: -32700,
  INVALID_REQUEST: -32600,
  METHOD_NOT_FOUND: -32601,
  INVALID_PARAMS: -32602,
  INTERNAL_ERROR: -32603,
  // A2A-specific (range -32000..-32099)
  AGENT_NOT_FOUND: -32001,
  TASK_NOT_FOUND: -32002,
  AGENT_UNAUTHORIZED: -32003,
  AGENT_UNAVAILABLE: -32004,
} as const;
export type A2AErrorCode = (typeof A2A_ERROR)[keyof typeof A2A_ERROR];
